import React from 'react';
import { flushSync } from 'react-dom';
import type { CanvasBlockLayoutChangeMeta, CanvasSectionBlock } from '../sectionRegistry';
import {
  buildDragAlignmentGuides,
  createAlignmentBoxFromLayout,
  createReferenceFrameBoxes,
  formatAlignmentDebugPayload,
  resolveAlignmentGuidePosition,
  serializeAlignmentBox,
  translateAlignmentBox,
  type AlignmentBox,
} from './alignmentMath';
import {
  buildFineMoveLiveGapBoxes,
  computeFineMoveGroupCenterClient,
  getFineMoveItems,
  reconcileFineMoveGuidesWithLiveMovingBox,
  isDragDuplicateModifier,
  isDragSnapDisabled,
  resolveDragAxisConstrainedDelta,
  resolveFineMovePreview,
} from './fineMove';
import { resolveFineMoveMagneticSnap } from './magneticSnap';
import {
  applyHandleDelta,
  applyMoveSnap,
  clampHandleLayoutToBounds,
  layoutsEqual,
} from './snap';
import type { LayoutSnapBlock } from './snap';
import type { AlignmentGuides, TransformHandle, TransformInteractionState } from './types';
import type { FineMoveInteractionState, UseBlockTransformEngineOptions } from './transformEngineTypes';

const PLACEMENT_POINTER_ID = 8742;
import {
  resolveAlignmentBoxSource,
  resolveEditorBoundsMode,
  resolveGapAlignmentBoxFromLayer,
  resolveLiveActiveBoxFromLayer,
  type EditorBoundsBox,
} from './editorBounds';
import { buildMovementGeometryPayload } from './geometryAudit';
import {
  readResponsiveBlockTransformStyle,
  type ResponsiveBlockTransformStyle,
  writeResponsiveBlockTransformStyle,
} from './transformStyle';
import {
  emitMovementDebug,
  getMovementDebugBlockSnapshot,
} from './movementDebug';

function alignmentGuideValuesEqual(a: AlignmentGuides['x'], b: AlignmentGuides['x']) {
  const aPosition = resolveAlignmentGuidePosition(a);
  const bPosition = resolveAlignmentGuidePosition(b);
  if (aPosition !== bPosition) return false;
  if (typeof a === 'number' || typeof b === 'number') return typeof a === typeof b;
  return (
    (a?.axis ?? null) === (b?.axis ?? null) &&
    (a?.kind ?? null) === (b?.kind ?? null) &&
    (a?.sourceId ?? null) === (b?.sourceId ?? null) &&
    (a?.label ?? null) === (b?.label ?? null)
  );
}

function alignmentGuidesEqual(a: AlignmentGuides, b: AlignmentGuides) {
  const spacingA = a.spacing ?? [];
  const spacingB = b.spacing ?? [];
  if (spacingA.length !== spacingB.length || Boolean(a.spacingEqual) !== Boolean(b.spacingEqual)) {
    return false;
  }
  for (let index = 0; index < spacingA.length; index += 1) {
    const left = spacingA[index];
    const right = spacingB[index];
    if (
      left.axis !== right.axis ||
      left.start !== right.start ||
      left.end !== right.end ||
      left.position !== right.position ||
      left.gap !== right.gap ||
      (left.equalGroupId ?? null) !== (right.equalGroupId ?? null) ||
      (left.placement ?? null) !== (right.placement ?? null) ||
      left.edgeMarkerStart !== right.edgeMarkerStart ||
      left.edgeMarkerEnd !== right.edgeMarkerEnd ||
      (left.labelPosition?.x ?? null) !== (right.labelPosition?.x ?? null) ||
      (left.labelPosition?.y ?? null) !== (right.labelPosition?.y ?? null) ||
      left.label !== right.label
    ) {
      return false;
    }
  }
  const linesA = a.lines ?? [];
  const linesB = b.lines ?? [];
  if (linesA.length !== linesB.length) return false;
  for (let index = 0; index < linesA.length; index += 1) {
    const left = linesA[index];
    const right = linesB[index];
    if (
      left.axis !== right.axis ||
      left.position !== right.position ||
      left.kind !== right.kind ||
      left.spanStart !== right.spanStart ||
      left.spanEnd !== right.spanEnd ||
      (left.sourceId ?? null) !== (right.sourceId ?? null)
    ) {
      return false;
    }
  }
  const activeXA = a.activeX ?? null;
  const activeXB = b.activeX ?? null;
  if (
    (activeXA?.movingAnchor ?? null) !== (activeXB?.movingAnchor ?? null) ||
    (activeXA?.targetBlockId ?? null) !== (activeXB?.targetBlockId ?? null) ||
    (activeXA?.targetAnchor ?? null) !== (activeXB?.targetAnchor ?? null) ||
    (activeXA?.distancePx ?? null) !== (activeXB?.distancePx ?? null)
  ) {
    return false;
  }
  const activeYA = a.activeY ?? null;
  const activeYB = b.activeY ?? null;
  if (
    (activeYA?.movingAnchor ?? null) !== (activeYB?.movingAnchor ?? null) ||
    (activeYA?.targetBlockId ?? null) !== (activeYB?.targetBlockId ?? null) ||
    (activeYA?.targetAnchor ?? null) !== (activeYB?.targetAnchor ?? null) ||
    (activeYA?.distancePx ?? null) !== (activeYB?.distancePx ?? null)
  ) {
    return false;
  }
  return alignmentGuideValuesEqual(a.x ?? null, b.x ?? null) && alignmentGuideValuesEqual(a.y ?? null, b.y ?? null);
}

function asStyleRecord(style: unknown): Record<string, unknown> {
  return style && typeof style === 'object' && !Array.isArray(style)
    ? { ...(style as Record<string, unknown>) }
    : {};
}

function isHiddenReferenceBlock(block: LayoutReferenceBlock) {
  return asStyleRecord(block.style).builderHidden === true;
}

type LayoutReferenceBlock = {
  id: string;
  layout: CanvasSectionBlock['layout'];
  style?: CanvasSectionBlock['style'];
};

function createMovingEditorBoxFromLayout(
  interaction: Pick<
    TransformInteractionState,
    'startLayout' | 'startEditorBox' | 'colWidth' | 'rowHeight'
  >,
  layout: CanvasSectionBlock['layout']
) {
  if (!interaction.startEditorBox) {
    return createAlignmentBoxFromLayout(layout, interaction.colWidth, interaction.rowHeight);
  }
  return translateAlignmentBox(
    interaction.startEditorBox,
    (layout.x - interaction.startLayout.x) * interaction.colWidth,
    (layout.y - interaction.startLayout.y) * interaction.rowHeight
  );
}

function finiteNumber(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function resolveFineMoveStartOffsets({
  layout,
  colWidth,
  rowHeight,
  editorBounds,
  transform,
  blockWidth,
  blockHeight,
}: {
  layout: CanvasSectionBlock['layout'];
  colWidth: number;
  rowHeight: number;
  editorBounds?: EditorBoundsBox;
  transform: ResponsiveBlockTransformStyle;
  blockWidth: number;
  blockHeight: number;
}) {
  const gridLeftPx = (layout.x - 1) * colWidth;
  const gridTopPx = (layout.y - 1) * rowHeight;

  if (typeof transform.offsetXPx === 'number') {
    return {
      startOffsetXPx: transform.offsetXPx,
      startOffsetYPx:
        typeof transform.offsetYPx === 'number'
          ? transform.offsetYPx
          : (transform.offsetY / 100) * blockHeight,
    };
  }

  if (transform.offsetX !== 0 || transform.offsetY !== 0) {
    return {
      startOffsetXPx: (transform.offsetX / 100) * blockWidth,
      startOffsetYPx: (transform.offsetY / 100) * blockHeight,
    };
  }

  if (editorBounds?.activeBox) {
    return {
      startOffsetXPx: editorBounds.activeBox.left - gridLeftPx,
      startOffsetYPx: editorBounds.activeBox.top - gridTopPx,
    };
  }

  return { startOffsetXPx: 0, startOffsetYPx: 0 };
}

function clampTextScaleFontSize(value: number) {
  return Math.max(8, Math.min(1000, Math.round(value * 100) / 100));
}

function resolveTextScaleStartFontSize(
  style: Record<string, unknown>,
  viewport: 'desktop' | 'tablet' | 'mobile',
  blockElement: Element | null
) {
  const computedTextElement = blockElement?.querySelector('[data-builder-text-content="true"]') as HTMLElement | null;
  const computedFontSize =
    computedTextElement && typeof window !== 'undefined'
      ? finiteNumber(window.getComputedStyle(computedTextElement).fontSize)
      : undefined;
  if (viewport === 'mobile') {
    return (
      finiteNumber(style.fontSizeMobile) ??
      finiteNumber(style.fontSizeDesktop) ??
      finiteNumber(style.fontSize) ??
      computedFontSize ??
      16
    );
  }
  return (
    finiteNumber(style.fontSizeDesktop) ??
    finiteNumber(style.fontSize) ??
    computedFontSize ??
    16
  );
}

function writeTextScaleFontSize(
  style: Record<string, unknown>,
  viewport: 'desktop' | 'tablet' | 'mobile',
  fontSize: number
) {
  const next = { ...style };
  if (viewport === 'mobile') {
    next.fontSizeMobile = fontSize;
    return next;
  }
  next.fontSize = fontSize;
  next.fontSizeDesktop = fontSize;
  return next;
}

function styleRecordsEqual(a: Record<string, unknown> | null, b: Record<string, unknown> | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function emitPostRenderMovementDebug(emit: () => void) {
  if (typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(emit);
  });
}

export function useBlockTransformEngine({
  enabled,
  isAdmin,
  layerRef,
  blocks,
  viewport = 'desktop',
  maxRows,
  allowRowExpansion = true,
  totalColumns,
  snapThresholdPx,
  rowHeightPx,
  selectedBlockIds = [],
  onSelectBlock,
  onBlockLayoutChange,
  onPreviewBlockLayout,
  onCommitBlockLayout,
  onPreviewBlockStyle,
  onCommitBlockStyle,
  onDuplicateCanvasBlockForDrag,
  onPlacementCancel,
  onPlacementCommit,
  editorBoundsByBlockId = {},
}: UseBlockTransformEngineOptions) {
  const transformRef = React.useRef<TransformInteractionState | null>(null);
  const autoScrollRafRef = React.useRef<number | null>(null);
  const fineMoveRafRef = React.useRef<number | null>(null);
  const fineMovePointerRef = React.useRef<{
    clientX: number;
    clientY: number;
    snapDisabled: boolean;
    shiftKey: boolean;
  } | null>(null);
  const [alignmentGuides, setAlignmentGuides] = React.useState<AlignmentGuides>({});
  const alignmentGuidesRef = React.useRef<AlignmentGuides>({});
  const [isTransforming, setIsTransforming] = React.useState(false);
  const [isPlacementDrag, setIsPlacementDrag] = React.useState(false);

  const fineMoveRef = React.useRef<FineMoveInteractionState | null>(null);
  const blockKindsById = React.useMemo(
    () => Object.fromEntries(blocks.map((block) => [block.id, block.type])),
    [blocks]
  );

  const quantizeResizeDelta = React.useCallback((delta: number) => {
    if (!Number.isFinite(delta) || Math.abs(delta) < 1) {
      return 0;
    }
    return delta < 0 ? Math.ceil(delta) : Math.floor(delta);
  }, []);

  const stopAutoScroll = React.useCallback(() => {
    if (autoScrollRafRef.current != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(autoScrollRafRef.current);
    }
    autoScrollRafRef.current = null;
  }, []);

  const resolveScrollContainer = React.useCallback(() => {
    const layer = layerRef.current;
    if (!layer) return null;
    return layer.closest('[data-builder-scroll-container="true"]') as HTMLElement | null;
  }, [layerRef]);

  const setAlignmentGuidesIfChanged = React.useCallback((next: AlignmentGuides) => {
    if (alignmentGuidesEqual(alignmentGuidesRef.current, next)) {
      return;
    }
    alignmentGuidesRef.current = next;
    setAlignmentGuides(next);
  }, []);

  const buildLiveReferenceFrameBoxes = React.useCallback(
    (referenceBlocks: LayoutSnapBlock[], colWidth: number, rowHeight: number) => {
      const layer = layerRef.current;
      const frameBoxesByBlockId: Record<string, AlignmentBox | undefined> = {};
      referenceBlocks.forEach((block) => {
        const sourceBlock = blocks.find((candidate) => candidate.id === block.id);
        const gapBox =
          layer && sourceBlock ? resolveGapAlignmentBoxFromLayer(sourceBlock, layer) : null;
        const registryBounds = editorBoundsByBlockId[block.id];
        frameBoxesByBlockId[block.id] =
          gapBox ?? registryBounds?.activeBox ?? registryBounds?.frameBox;
      });
      return createReferenceFrameBoxes(referenceBlocks, colWidth, rowHeight, frameBoxesByBlockId);
    },
    [blocks, editorBoundsByBlockId, layerRef]
  );

  const applyTransformAtPointer = React.useCallback(
    (
      interaction: TransformInteractionState,
      clientX: number,
      clientY: number,
      snapDisabled = false,
      shiftKey = false
    ) => {
      const applyLayoutChange = onPreviewBlockLayout ?? onBlockLayoutChange;
      if (!applyLayoutChange) return;
      const scrollContainer = resolveScrollContainer();
      const scrollDeltaLeft = scrollContainer
        ? scrollContainer.scrollLeft - interaction.startScrollLeft
        : 0;
      const scrollDeltaTop = scrollContainer
        ? scrollContainer.scrollTop - interaction.startScrollTop
        : 0;
      const rawDeltaCols =
        (clientX - interaction.startClientX + scrollDeltaLeft) / interaction.colWidth;
      const rawDeltaRows =
        (clientY - interaction.startClientY + scrollDeltaTop) / interaction.rowHeight;
      const isMoveHandle = interaction.handle === 'move';
      const axisConstrained =
        isMoveHandle
          ? resolveDragAxisConstrainedDelta(rawDeltaCols, rawDeltaRows, shiftKey)
          : {
              deltaXPx: rawDeltaCols,
              deltaYPx: rawDeltaRows,
              axisLock: null,
            };
      const deltaCols = isMoveHandle
        ? Math.round(axisConstrained.deltaXPx)
        : quantizeResizeDelta(rawDeltaCols);
      const deltaRows = isMoveHandle
        ? Math.round(axisConstrained.deltaYPx)
        : quantizeResizeDelta(rawDeltaRows);
      const tentativeLayout = applyHandleDelta(
        interaction.startLayout,
        interaction.handle,
        deltaCols,
        deltaRows
      );
      const dynamicMaxRows = allowRowExpansion
        ? Math.max(
            interaction.maxRows,
            Math.round(tentativeLayout.y) + Math.round(tentativeLayout.h)
          )
        : interaction.maxRows;
      const next = clampHandleLayoutToBounds(
        interaction.startLayout,
        interaction.handle,
        tentativeLayout,
        dynamicMaxRows,
        totalColumns
      );
      const snapped =
        snapDisabled && isMoveHandle
          ? { layout: next, guides: {} as AlignmentGuides }
          : applyMoveSnap({
              nextLayout: next,
              interaction,
              blockId: interaction.blockId,
              blocks: interaction.referenceBlocks,
              totalColumns,
              snapThresholdPx,
              maxRows: dynamicMaxRows,
            });
      if (isMoveHandle) {
        const referenceBoxes = buildLiveReferenceFrameBoxes(
          interaction.referenceBlocks,
          interaction.colWidth,
          interaction.rowHeight
        );
        const movingBox = createMovingEditorBoxFromLayout(interaction, snapped.layout);
        const gridMovingBlock = blocks.find((candidate) => candidate.id === interaction.blockId);
        const sectionWidth = interaction.colWidth * totalColumns;
        const sectionHeight = interaction.rowHeight * dynamicMaxRows;
        const movingBoxSource = gridMovingBlock
          ? resolveAlignmentBoxSource(
              gridMovingBlock,
              editorBoundsByBlockId[gridMovingBlock.id]
            )
          : 'layout';
        const previewAlignment = buildDragAlignmentGuides({
          movingBox,
          referenceBoxes,
          sectionWidth,
          sectionHeight,
          threshold: snapThresholdPx,
          applySnapToPosition: false,
          movingBlockId: interaction.blockId,
          movingBoxSource,
          blockKindsById,
        });
        const magnetic = resolveFineMoveMagneticSnap({
          rawPreviewBox: movingBox,
          activeXGuide: previewAlignment.analysis.activeXGuide,
          activeYGuide: previewAlignment.analysis.activeYGuide,
          referenceBoxes,
          sectionWidth,
          movingBlockId: interaction.blockId,
          snapDisabled,
          verticalAlignmentPriority:
            previewAlignment.analysis.verticalAlignmentDebug?.activeYGuide?.priority,
          rowPeerIds: previewAlignment.analysis.rowPeerIds,
        });
        const liveAlignment = buildDragAlignmentGuides({
          movingBox,
          referenceBoxes,
          sectionWidth,
          sectionHeight,
          threshold: snapThresholdPx,
          applySnapToPosition: false,
          movingBlockId: interaction.blockId,
          movingBoxSource,
          blockKindsById,
          magneticDisplayBox: magnetic.magnetisedPreviewBox,
        });
        setAlignmentGuidesIfChanged(liveAlignment.guides);
      } else {
        setAlignmentGuidesIfChanged(snapped.guides);
      }
      if (interaction.textScale && !isMoveHandle && onPreviewBlockStyle) {
        const widthRatio = Math.max(0.05, snapped.layout.w / Math.max(1, interaction.startLayout.w));
        const nextFontSize = clampTextScaleFontSize(interaction.textScale.startFontSize * widthRatio);
        const nextStyle = writeTextScaleFontSize(
          interaction.textScale.startStyle,
          viewport,
          nextFontSize
        );
        if (!styleRecordsEqual(nextStyle, interaction.textScale.lastAppliedStyle)) {
          onPreviewBlockStyle(interaction.blockId, nextStyle);
          interaction.textScale.lastAppliedStyle = nextStyle;
          interaction.textScale.pendingStyle = nextStyle;
        }
      }
      if (!layoutsEqual(snapped.layout, interaction.lastAppliedLayout)) {
        const nextMeta: CanvasBlockLayoutChangeMeta | undefined =
          interaction.blockType === 'product-card' && interaction.handle !== 'move'
            ? {
                contentOffsetX:
                  (interaction.startContentOffsetX ?? 0) -
                  (snapped.layout.x - interaction.startLayout.x),
                contentOffsetY:
                  (interaction.startContentOffsetY ?? 0) -
                  (snapped.layout.y - interaction.startLayout.y),
              }
            : undefined;
        applyLayoutChange(interaction.blockId, snapped.layout, nextMeta);
        emitMovementDebug({
          source: interaction.handle === 'move' ? 'drag' : 'resize',
          phase: 'preview',
          blockId: interaction.blockId,
          viewport,
          handle: interaction.handle,
          input: { clientX, clientY },
          delta: { cols: deltaCols, rows: deltaRows },
          layout: snapped.layout,
          dom: getMovementDebugBlockSnapshot(interaction.blockId),
        });
        interaction.lastAppliedLayout = { ...snapped.layout };
        interaction.pendingLayout = { ...snapped.layout };
        interaction.pendingMeta = nextMeta;
      }
    },
    [
      allowRowExpansion,
      blockKindsById,
      onBlockLayoutChange,
      onPreviewBlockLayout,
      onPreviewBlockStyle,
      blocks,
      buildLiveReferenceFrameBoxes,
      editorBoundsByBlockId,
      quantizeResizeDelta,
      resolveScrollContainer,
      setAlignmentGuidesIfChanged,
      snapThresholdPx,
      totalColumns,
      viewport,
    ]
  );

  const readTransform = React.useCallback(
    (style: Record<string, unknown>) =>
      readResponsiveBlockTransformStyle(style, viewport, {
        isolateViewport: isAdmin && viewport !== 'desktop',
      }),
    [isAdmin, viewport]
  );

  const writeTransform = React.useCallback(
    (style: Record<string, unknown>, transform: Record<string, number | null>) =>
      writeResponsiveBlockTransformStyle(style, transform, viewport),
    [viewport]
  );

  const createReferenceBlocks = React.useCallback(
    (activeBlockId: string | string[], colWidth: number, rowHeight: number) => {
      const activeBlockIds = new Set(Array.isArray(activeBlockId) ? activeBlockId : [activeBlockId]);
      return (
      blocks
        .filter((candidate) => !activeBlockIds.has(candidate.id))
        .filter((candidate) => !isHiddenReferenceBlock(candidate as LayoutReferenceBlock))
        .map((candidate) => {
          const style = asStyleRecord((candidate as LayoutReferenceBlock).style);
          const transform = readTransform(style);
          const editorBounds = editorBoundsByBlockId[candidate.id];
          const activeBox = editorBounds?.activeBox ?? editorBounds?.frameBox;
          const widthPx = activeBox
            ? activeBox.width
            : Math.max(1, candidate.layout.w * colWidth * transform.widthScale);
          const heightPx = activeBox
            ? activeBox.height
            : Math.max(1, candidate.layout.h * rowHeight * transform.heightScale);
          const offsetXPx =
            typeof transform.offsetXPx === 'number'
              ? transform.offsetXPx
              : (transform.offsetX / 100) * widthPx;
          const offsetYPx =
            typeof transform.offsetYPx === 'number'
              ? transform.offsetYPx
              : (transform.offsetY / 100) * heightPx;
          return {
            id: candidate.id,
            layout: { ...candidate.layout },
            leftPx: activeBox?.left,
            topPx: activeBox?.top,
            offsetXPx: activeBox ? undefined : offsetXPx,
            offsetYPx: activeBox ? undefined : offsetYPx,
            widthPx,
            heightPx,
          };
        })
      );
    },
    [blocks, editorBoundsByBlockId, readTransform]
  );

  const applyFineMoveAtPointer = React.useCallback(
    (clientX: number, clientY: number, snapDisabled = false, shiftKey = false) => {
      const interaction = fineMoveRef.current;
      if (!interaction || !onPreviewBlockStyle) return [];
      const dragViewport = interaction.viewport ?? viewport;
      const readDragTransform = (style: Record<string, unknown>) =>
        readResponsiveBlockTransformStyle(style, dragViewport);
      const writeDragTransform = (
        style: Record<string, unknown>,
        transform: Record<string, number | null>
      ) => writeResponsiveBlockTransformStyle(style, transform, dragViewport);
      const referenceBoxes = buildLiveReferenceFrameBoxes(
        interaction.referenceBlocks,
        interaction.colWidth,
        interaction.rowHeight
      );
      const primaryBlock = blocks.find((candidate) => candidate.id === interaction.blockId);
      const movingBoxSource = primaryBlock
        ? resolveAlignmentBoxSource(primaryBlock, editorBoundsByBlockId[primaryBlock.id])
        : 'layout';
      const preview = resolveFineMovePreview({
        interaction,
        clientX,
        clientY,
        snapDisabled,
        shiftKey,
        referenceBoxes,
        blockKindsById,
        movingBoxSource,
        readTransform: readDragTransform,
        writeTransform: writeDragTransform,
      });
      const groupItems = getFineMoveItems(interaction);
      interaction.lastMagneticSnapDebug = preview.magneticSnapDebug;

      flushSync(() => {
        preview.updates.forEach(({ blockId, nextStyle }) => {
          const item = groupItems.find((candidate) => candidate.blockId === blockId);
          if (item) {
            item.nextStyle = nextStyle;
          }
          if (blockId === interaction.blockId) {
            interaction.nextStyle = nextStyle;
          }
          onPreviewBlockStyle(blockId, nextStyle);
        });
      });

      let guides = preview.guides;
      let movingBox = preview.movingBox;
      let analysis = preview.analysis;
      const layer = layerRef.current;
      const liveGapBoxes =
        layer && primaryBlock
          ? buildFineMoveLiveGapBoxes({
              layer,
              blocks,
              referenceBlocks: interaction.referenceBlocks,
              movingBlockId: interaction.blockId,
            })
          : null;
      const liveMovingBox = liveGapBoxes?.movingBox ?? null;
      const liveReferenceBoxes = liveGapBoxes?.referenceBoxes;
      if (liveMovingBox && analysis) {
        const gapReferenceBoxes =
          liveReferenceBoxes && liveReferenceBoxes.length > 0 ? liveReferenceBoxes : referenceBoxes;
        const reconciled = reconcileFineMoveGuidesWithLiveMovingBox({
          analysis,
          liveMovingBox,
          referenceBoxes: gapReferenceBoxes,
          sectionWidth: interaction.colWidth * interaction.totalColumns,
          movingBlockId: interaction.blockId,
          movingBoxSource,
        });
        guides = reconciled.guides;
        analysis = reconciled.analysis;
        movingBox = reconciled.movingBox;
        if (preview.magneticSnapDebug) {
          preview.magneticSnapDebug = {
            ...preview.magneticSnapDebug,
            magnetisedPreviewBox: serializeAlignmentBox(liveMovingBox),
          };
          interaction.lastMagneticSnapDebug = preview.magneticSnapDebug;
        }
      }

      setAlignmentGuidesIfChanged(guides);
      const primaryItem = groupItems.find((item) => item.blockId === interaction.blockId);
      const geometryPayload =
        primaryBlock
          ? buildMovementGeometryPayload({
              block: primaryBlock,
              blocks,
              layer: layerRef.current,
              colWidth: interaction.colWidth,
              rowHeight: interaction.rowHeight,
              editorBoundsByBlockId,
              dragStartBox: primaryItem?.startFrameBox ?? interaction.startFrameBox,
              previewMovingBox: movingBox ?? analysis?.movingBox,
              analysis,
            })
          : undefined;

      emitMovementDebug({
        source: 'drag',
        phase: 'preview',
        blockId: interaction.blockId,
        blockIds: groupItems.map((item) => item.blockId),
        viewport,
        handle: 'move',
        movementBranch: 'fine-style-offset',
        input: { clientX, clientY, snapDisabled, shiftKey, axisLock: preview.axisLock },
        delta: {
          xPx: clientX - interaction.startClientX,
          yPx: clientY - interaction.startClientY,
        },
        updates: preview.updates.map(({ blockId, nextStyle }) => ({
          blockId,
          transform: readTransform(nextStyle),
          dom: getMovementDebugBlockSnapshot(blockId),
        })),
        dom: getMovementDebugBlockSnapshot(interaction.blockId),
        alignment: analysis
          ? formatAlignmentDebugPayload(analysis, preview.magneticSnapDebug?.snapApplied)
          : undefined,
        magneticSnapDebug: preview.magneticSnapDebug,
        geometryAudit: geometryPayload?.geometryAudit,
        candidateGeometry: geometryPayload?.candidateGeometry,
      });
      return preview.updates;
    },
    [
      blockKindsById,
      blocks,
      buildLiveReferenceFrameBoxes,
      editorBoundsByBlockId,
      layerRef,
      onPreviewBlockStyle,
      readTransform,
      setAlignmentGuidesIfChanged,
      snapThresholdPx,
      viewport,
      writeTransform,
    ]
  );

  const flushFineMovePreview = React.useCallback(() => {
    fineMoveRafRef.current = null;
    const pointer = fineMovePointerRef.current;
    if (!pointer) return;
    applyFineMoveAtPointer(pointer.clientX, pointer.clientY, pointer.snapDisabled, pointer.shiftKey);
  }, [applyFineMoveAtPointer]);

  const scheduleFineMovePreview = React.useCallback(
    (clientX: number, clientY: number, snapDisabled: boolean, shiftKey = false) => {
      fineMovePointerRef.current = { clientX, clientY, snapDisabled, shiftKey };
      if (fineMoveRafRef.current != null || typeof window === 'undefined') {
        return;
      }
      fineMoveRafRef.current = window.requestAnimationFrame(flushFineMovePreview);
    },
    [flushFineMovePreview]
  );

  const finishFineMove = React.useCallback(
    (pointerId: number, clientX: number, clientY: number, snapDisabled: boolean, shiftKey = false) => {
      const fineMove = fineMoveRef.current;
      if (!fineMove || fineMove.pointerId !== pointerId) return false;
      if (fineMoveRafRef.current != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(fineMoveRafRef.current);
        fineMoveRafRef.current = null;
      }
      fineMovePointerRef.current = null;
      const deltaXPx = clientX - fineMove.startClientX;
      const deltaYPx = clientY - fineMove.startClientY;
      if (Math.hypot(deltaXPx, deltaYPx) < 0.5 && !fineMove.isPlacementDrag) {
        getFineMoveItems(fineMove).forEach((item) => {
          onPreviewBlockStyle?.(item.blockId, null);
        });
        fineMoveRef.current = null;
        setIsPlacementDrag(false);
        setIsTransforming(false);
        setAlignmentGuidesIfChanged({});
        return true;
      }
      applyFineMoveAtPointer(clientX, clientY, snapDisabled, shiftKey);
      const updatesToCommit = getFineMoveItems(fineMove).map((item) => ({
        blockId: item.blockId,
        nextStyle: item.nextStyle ?? item.startStyle,
      }));
      const committedUpdates = updatesToCommit.map(({ blockId, nextStyle }) => ({
        blockId,
        nextStyle,
        transform: readTransform(nextStyle),
      }));
      flushSync(() => {
        updatesToCommit.forEach(({ blockId, nextStyle }) => {
          onCommitBlockStyle?.(blockId, nextStyle, fineMove.viewport);
          onPreviewBlockStyle?.(blockId, null);
        });
      });
      const committedBlock = blocks.find((candidate) => candidate.id === fineMove.blockId);
      const committedGeometry =
        committedBlock
          ? buildMovementGeometryPayload({
              block: committedBlock,
              blocks,
              layer: layerRef.current,
              colWidth: fineMove.colWidth,
              rowHeight: fineMove.rowHeight,
              editorBoundsByBlockId,
              dragStartBox: fineMove.startFrameBox,
              committedBox:
                layerRef.current && committedBlock
                  ? resolveLiveActiveBoxFromLayer(
                      layerRef.current,
                      committedBlock.id,
                      committedBlock
                    )
                  : undefined,
            })
          : undefined;
      emitMovementDebug({
        source: 'drag',
        phase: 'commit',
        blockId: fineMove.blockId,
        blockIds: getFineMoveItems(fineMove).map((item) => item.blockId),
        viewport,
        handle: 'move',
        movementBranch: 'fine-style-offset',
        input: { clientX, clientY, snapDisabled, shiftKey },
        delta: {
          xPx: clientX - fineMove.startClientX,
          yPx: clientY - fineMove.startClientY,
        },
        updates: committedUpdates.map(({ blockId, transform }) => ({
          blockId,
          transform,
          dom: getMovementDebugBlockSnapshot(blockId),
        })),
        dom: getMovementDebugBlockSnapshot(fineMove.blockId),
        magneticSnapDebug: fineMove.lastMagneticSnapDebug,
        geometryAudit: committedGeometry?.geometryAudit,
        candidateGeometry: committedGeometry?.candidateGeometry,
      });
      emitPostRenderMovementDebug(() => {
        const postCommitGeometry =
          committedBlock
            ? buildMovementGeometryPayload({
                block: committedBlock,
                blocks,
                layer: layerRef.current,
                colWidth: fineMove.colWidth,
                rowHeight: fineMove.rowHeight,
                editorBoundsByBlockId,
                dragStartBox: fineMove.startFrameBox,
                committedBox: layerRef.current
                  ? resolveLiveActiveBoxFromLayer(
                      layerRef.current,
                      committedBlock.id,
                      committedBlock
                    )
                  : undefined,
              })
            : undefined;
        emitMovementDebug({
          source: 'drag',
          phase: 'post-commit',
          blockId: fineMove.blockId,
          blockIds: getFineMoveItems(fineMove).map((item) => item.blockId),
          viewport,
          handle: 'move',
          input: { clientX, clientY, snapDisabled },
          delta: {
            xPx: clientX - fineMove.startClientX,
            yPx: clientY - fineMove.startClientY,
          },
          updates: committedUpdates.map(({ blockId, transform }) => ({
            blockId,
            transform,
            dom: getMovementDebugBlockSnapshot(blockId),
          })),
          dom: getMovementDebugBlockSnapshot(fineMove.blockId),
          note: 'DOM after preview override cleared and committed style rendered.',
          geometryAudit: postCommitGeometry?.geometryAudit,
          candidateGeometry: postCommitGeometry?.candidateGeometry,
        });
      });
      if (fineMove.isPlacementDrag) {
        const placementNudgeXPx = fineMove.placementNudgeXPx ?? 0;
        const placementNudgeYPx = fineMove.placementNudgeYPx ?? 0;
        onPlacementCommit?.({
          blockIds:
            fineMove.placementBlockIds ?? getFineMoveItems(fineMove).map((item) => item.blockId),
          primaryBlockId: fineMove.blockId,
          referenceBlockId: fineMove.placementReferenceBlockId,
          deltaXPx: clientX - fineMove.startClientX + placementNudgeXPx,
          deltaYPx: clientY - fineMove.startClientY + placementNudgeYPx,
        });
        setIsPlacementDrag(false);
      }
      fineMoveRef.current = null;
      setIsTransforming(false);
      setAlignmentGuidesIfChanged({});
      return true;
    },
    [
      applyFineMoveAtPointer,
      blocks,
      editorBoundsByBlockId,
      layerRef,
      onCommitBlockStyle,
      onPlacementCommit,
      onPreviewBlockStyle,
      readTransform,
      setAlignmentGuidesIfChanged,
      setIsPlacementDrag,
      setIsTransforming,
      viewport,
    ]
  );

  const cancelPlacementDrag = React.useCallback(() => {
    const fineMove = fineMoveRef.current;
    if (!fineMove?.isPlacementDrag) return false;
    if (fineMoveRafRef.current != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(fineMoveRafRef.current);
      fineMoveRafRef.current = null;
    }
    fineMovePointerRef.current = null;
    const blockIds =
      fineMove.placementBlockIds ?? getFineMoveItems(fineMove).map((item) => item.blockId);
    getFineMoveItems(fineMove).forEach((item) => {
      onPreviewBlockStyle?.(item.blockId, null);
    });
    fineMoveRef.current = null;
    setIsPlacementDrag(false);
    setIsTransforming(false);
    setAlignmentGuidesIfChanged({});
    onPlacementCancel?.(blockIds);
    return true;
  }, [
    onPlacementCancel,
    onPreviewBlockStyle,
    setAlignmentGuidesIfChanged,
    setIsPlacementDrag,
    setIsTransforming,
  ]);

  const nudgePlacementDrag = React.useCallback(
    (deltaXPx: number, deltaYPx: number) => {
      const fineMove = fineMoveRef.current;
      if (!fineMove?.isPlacementDrag) return;
      fineMove.placementNudgeXPx = (fineMove.placementNudgeXPx ?? 0) + deltaXPx;
      fineMove.placementNudgeYPx = (fineMove.placementNudgeYPx ?? 0) + deltaYPx;
      const pointer = fineMovePointerRef.current;
      applyFineMoveAtPointer(
        pointer?.clientX ?? fineMove.startClientX,
        pointer?.clientY ?? fineMove.startClientY,
        pointer?.snapDisabled ?? false,
        pointer?.shiftKey ?? false
      );
    },
    [applyFineMoveAtPointer]
  );

  const commitPlacementDrag = React.useCallback(() => {
    const fineMove = fineMoveRef.current;
    if (!fineMove?.isPlacementDrag) return false;
    const pointer = fineMovePointerRef.current;
    return finishFineMove(
      PLACEMENT_POINTER_ID,
      pointer?.clientX ?? fineMove.startClientX,
      pointer?.clientY ?? fineMove.startClientY,
      pointer?.snapDisabled ?? false,
      pointer?.shiftKey ?? false
    );
  }, [finishFineMove]);

  const finishLayoutTransform = React.useCallback(
    (pointerId: number) => {
      const interaction = transformRef.current;
      if (!interaction || interaction.pointerId !== pointerId) return false;
      if (!layoutsEqual(interaction.pendingLayout, interaction.startLayout)) {
        (onCommitBlockLayout ?? onBlockLayoutChange)?.(
          interaction.blockId,
          interaction.pendingLayout,
          {
            ...interaction.pendingMeta,
            viewport: interaction.viewport,
          }
        );
        emitMovementDebug({
          source: interaction.handle === 'move' ? 'drag' : 'resize',
          phase: 'commit',
          blockId: interaction.blockId,
          viewport,
          handle: interaction.handle,
          layout: interaction.pendingLayout,
          dom: getMovementDebugBlockSnapshot(interaction.blockId),
        });
      }
      if (
        interaction.textScale?.pendingStyle &&
        !styleRecordsEqual(interaction.textScale.pendingStyle, interaction.textScale.startStyle)
      ) {
        onCommitBlockStyle?.(
          interaction.blockId,
          interaction.textScale.pendingStyle,
          interaction.viewport
        );
      }
      onPreviewBlockLayout?.(interaction.blockId, null);
      if (interaction.textScale) {
        onPreviewBlockStyle?.(interaction.blockId, null);
      }
      transformRef.current = null;
      setIsTransforming(false);
      setAlignmentGuidesIfChanged({});
      stopAutoScroll();
      return true;
    },
    [
      onBlockLayoutChange,
      onCommitBlockLayout,
      onCommitBlockStyle,
      onPreviewBlockLayout,
      onPreviewBlockStyle,
      setAlignmentGuidesIfChanged,
      stopAutoScroll,
    ]
  );

  const stepAutoScroll = React.useCallback(() => {
    const interaction = transformRef.current;
    const scrollContainer = resolveScrollContainer();
    if (!interaction || !scrollContainer || !enabled) {
      autoScrollRafRef.current = null;
      return;
    }

    const rect = scrollContainer.getBoundingClientRect();
    const threshold = 120;
    const maxStep = 8;
    const minStep = 0.35;
    let deltaX = 0;
    let deltaY = 0;

    if (interaction.lastClientY > rect.bottom - threshold) {
      const ratio = Math.min(1, (interaction.lastClientY - (rect.bottom - threshold)) / threshold);
      deltaY = minStep + ratio * ratio * ratio * (maxStep - minStep);
    } else if (interaction.lastClientY < rect.top + threshold) {
      const ratio = Math.min(1, ((rect.top + threshold) - interaction.lastClientY) / threshold);
      deltaY = -(minStep + ratio * ratio * ratio * (maxStep - minStep));
    }

    if (interaction.lastClientX > rect.right - threshold) {
      const ratio = Math.min(1, (interaction.lastClientX - (rect.right - threshold)) / threshold);
      deltaX = minStep + ratio * ratio * ratio * (maxStep - minStep);
    } else if (interaction.lastClientX < rect.left + threshold) {
      const ratio = Math.min(1, ((rect.left + threshold) - interaction.lastClientX) / threshold);
      deltaX = -(minStep + ratio * ratio * ratio * (maxStep - minStep));
    }

    if (deltaX !== 0 || deltaY !== 0) {
      const nextScrollLeft = Math.max(
        0,
        Math.min(
          scrollContainer.scrollWidth - scrollContainer.clientWidth,
          scrollContainer.scrollLeft + deltaX
        )
      );
      const nextScrollTop = Math.max(
        0,
        Math.min(
          scrollContainer.scrollHeight - scrollContainer.clientHeight,
          scrollContainer.scrollTop + deltaY
        )
      );

      const didScroll =
        nextScrollLeft !== scrollContainer.scrollLeft || nextScrollTop !== scrollContainer.scrollTop;

      if (didScroll) {
        scrollContainer.scrollLeft = nextScrollLeft;
        scrollContainer.scrollTop = nextScrollTop;
        applyTransformAtPointer(interaction, interaction.lastClientX, interaction.lastClientY);
      }
    }

    autoScrollRafRef.current = window.requestAnimationFrame(stepAutoScroll);
  }, [applyTransformAtPointer, enabled, resolveScrollContainer]);

  const selectBlock = React.useCallback(
    (id: string) => {
      if (!isAdmin) return;
      onSelectBlock?.(id);
    },
    [isAdmin, onSelectBlock]
  );

  const beginPlacementDrag = React.useCallback(
    (
      activeBlock: LayoutSnapBlock,
      clientX: number,
      clientY: number,
      options?: {
        groupBlockIds?: string[];
        placementBlockIds?: string[];
        placementReferenceBlockId?: string;
      }
    ) => {
      if (!enabled || !onPreviewBlockStyle || !onCommitBlockStyle) return;
      const layer = layerRef.current;
      if (!layer) return;
      const rect = layer.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const colWidth = rect.width / totalColumns;
      const rowHeight = rowHeightPx ?? rect.height / maxRows;
      const primaryEditorBounds = editorBoundsByBlockId[activeBlock.id];
      const primaryGapStartBox = resolveGapAlignmentBoxFromLayer(activeBlock, layer);
      const primaryLiveFrame = resolveLiveActiveBoxFromLayer(layer, activeBlock.id, activeBlock);
      const selectedMoveIds =
        options?.groupBlockIds && options.groupBlockIds.length > 0
          ? options.groupBlockIds
          : [activeBlock.id];
      const moveBlocks = selectedMoveIds
        .map((blockId) => {
          if (blockId === activeBlock.id) {
            return blocks.find((candidate) => candidate.id === blockId) ?? activeBlock;
          }
          return blocks.find((candidate) => candidate.id === blockId);
        })
        .filter((candidate): candidate is LayoutSnapBlock => Boolean(candidate))
        .filter((candidate) => {
          if (candidate.id === activeBlock.id) return true;
          const style = asStyleRecord(candidate.style);
          return style.builderLocked !== true && style.builderHidden !== true;
        });
      const startStyle = asStyleRecord(activeBlock.style);
      const transform = readTransform(startStyle);
      const primaryBoundsMode = resolveEditorBoundsMode(activeBlock);
      const usesContentBounds =
        primaryBoundsMode === 'content' || primaryBoundsMode === 'text-paragraph';
      const primaryStartGapBox =
        primaryGapStartBox ??
        primaryLiveFrame ??
        primaryEditorBounds?.activeBox ??
        primaryEditorBounds?.frameBox;
      const resolvedBlockWidth = Math.max(
        1,
        primaryStartGapBox?.width ??
          (usesContentBounds ? undefined : primaryEditorBounds?.frameBox.width) ??
          activeBlock.layout.w * colWidth
      );
      const resolvedBlockHeight = Math.max(
        1,
        primaryStartGapBox?.height ??
          (usesContentBounds ? undefined : primaryEditorBounds?.frameBox.height) ??
          activeBlock.layout.h * rowHeight
      );
      const primaryStartFrameBox = primaryStartGapBox ?? undefined;
      const { startOffsetXPx, startOffsetYPx } = resolveFineMoveStartOffsets({
        layout: activeBlock.layout,
        colWidth,
        rowHeight,
        editorBounds: primaryEditorBounds,
        transform,
        blockWidth: resolvedBlockWidth,
        blockHeight: resolvedBlockHeight,
      });
      const groupItems = moveBlocks.map((moveBlock) => {
        const itemStartStyle = asStyleRecord(moveBlock.style);
        const itemTransform = readTransform(itemStartStyle);
        const isPrimary = moveBlock.id === activeBlock.id;
        const itemEditorBounds = editorBoundsByBlockId[moveBlock.id];
        const itemGapStartBox = resolveGapAlignmentBoxFromLayer(moveBlock, layer);
        const itemLiveFrame = resolveLiveActiveBoxFromLayer(layer, moveBlock.id, moveBlock);
        const itemBoundsMode = resolveEditorBoundsMode(moveBlock);
        const itemUsesContentBounds =
          itemBoundsMode === 'content' || itemBoundsMode === 'text-paragraph';
        const itemStartGapBox =
          itemGapStartBox ?? itemLiveFrame ?? itemEditorBounds?.activeBox ?? itemEditorBounds?.frameBox;
        const itemWidth = isPrimary
          ? resolvedBlockWidth
          : Math.max(
              1,
              itemStartGapBox?.width ??
                (itemUsesContentBounds ? undefined : itemEditorBounds?.frameBox.width) ??
                moveBlock.layout.w * colWidth * itemTransform.widthScale
            );
        const itemHeight = isPrimary
          ? resolvedBlockHeight
          : Math.max(
              1,
              itemStartGapBox?.height ??
                (itemUsesContentBounds ? undefined : itemEditorBounds?.frameBox.height) ??
                moveBlock.layout.h * rowHeight * itemTransform.heightScale
            );
        const { startOffsetXPx: itemOffsetXPx, startOffsetYPx: itemOffsetYPx } = resolveFineMoveStartOffsets({
          layout: moveBlock.layout,
          colWidth,
          rowHeight,
          editorBounds: itemEditorBounds,
          transform: itemTransform,
          blockWidth: itemWidth,
          blockHeight: itemHeight,
        });
        return {
          blockId: moveBlock.id,
          startLayout: { ...moveBlock.layout },
          startStyle: itemStartStyle,
          startOffsetXPx: itemOffsetXPx,
          startOffsetYPx: itemOffsetYPx,
          startEditorBox: itemStartGapBox,
          startFrameBox: itemStartGapBox,
          blockWidth: itemWidth,
          blockHeight: itemHeight,
          nextStyle: itemStartStyle,
        };
      });
      const groupCenter = computeFineMoveGroupCenterClient(groupItems, colWidth, rowHeight, rect);
      fineMovePointerRef.current = { clientX, clientY, snapDisabled: false, shiftKey: false };
      fineMoveRef.current = {
        pointerId: PLACEMENT_POINTER_ID,
        blockId: activeBlock.id,
        viewport,
        startLayout: { ...activeBlock.layout },
        startClientX: groupCenter.x,
        startClientY: groupCenter.y,
        startStyle,
        startOffsetXPx,
        startOffsetYPx,
        startEditorBox: primaryStartFrameBox,
        startFrameBox: primaryStartFrameBox,
        colWidth,
        rowHeight,
        maxRows,
        totalColumns,
        referenceBlocks: createReferenceBlocks(selectedMoveIds, colWidth, rowHeight),
        blockWidth: resolvedBlockWidth,
        blockHeight: resolvedBlockHeight,
        nextStyle: startStyle,
        groupItems,
        isPlacementDrag: true,
        placementBlockIds: options?.placementBlockIds ?? selectedMoveIds,
        placementReferenceBlockId: options?.placementReferenceBlockId,
        placementNudgeXPx: 0,
        placementNudgeYPx: 0,
      };
      setIsPlacementDrag(true);
      setIsTransforming(true);
      setAlignmentGuidesIfChanged({});
      applyFineMoveAtPointer(clientX, clientY, false, false);
    },
    [
      applyFineMoveAtPointer,
      blocks,
      createReferenceBlocks,
      enabled,
      editorBoundsByBlockId,
      layerRef,
      maxRows,
      onCommitBlockStyle,
      onPreviewBlockStyle,
      readTransform,
      rowHeightPx,
      setAlignmentGuidesIfChanged,
      setIsPlacementDrag,
      setIsTransforming,
      totalColumns,
    ]
  );

  const beginTransform = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, block: CanvasSectionBlock, handle: TransformHandle) => {
      if (!enabled || !onBlockLayoutChange) return;
      const layer = layerRef.current;
      if (!layer) return;
      const rect = layer.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      let activeBlock = block;
      if (
        handle === 'move' &&
        isDragDuplicateModifier(event) &&
        onDuplicateCanvasBlockForDrag &&
        selectedBlockIds.length <= 1
      ) {
        const duplicatedBlock = onDuplicateCanvasBlockForDrag(block.id);
        if (duplicatedBlock) {
          activeBlock = duplicatedBlock;
        }
      }

      if (!selectedBlockIds.includes(activeBlock.id)) {
        selectBlock(activeBlock.id);
      }

      if (handle === 'move' && onPreviewBlockStyle && onCommitBlockStyle) {
        const blockRoot = event.currentTarget.closest(
          '[data-builder-block-root="true"]'
        ) as HTMLElement | null;
        const blockRect = blockRoot?.getBoundingClientRect();
        const colWidth = rect.width / totalColumns;
        const rowHeight = rowHeightPx ?? rect.height / maxRows;
        const primaryEditorBounds = editorBoundsByBlockId[activeBlock.id];
        const primaryGapStartBox = resolveGapAlignmentBoxFromLayer(activeBlock, layer);
        const primaryLiveFrame = resolveLiveActiveBoxFromLayer(layer, activeBlock.id, activeBlock);
        const selectedMoveIds =
          isDragDuplicateModifier(event) && activeBlock.id !== block.id
            ? [activeBlock.id]
            : selectedBlockIds.length > 1 && selectedBlockIds.includes(activeBlock.id)
              ? selectedBlockIds
              : [activeBlock.id];
        const moveBlocks = selectedMoveIds
          .map((blockId) => {
            if (blockId === activeBlock.id) {
              return blocks.find((candidate) => candidate.id === blockId) ?? activeBlock;
            }
            return blocks.find((candidate) => candidate.id === blockId);
          })
          .filter((candidate): candidate is LayoutSnapBlock => Boolean(candidate))
          .filter((candidate) => {
            if (candidate.id === activeBlock.id) return true;
            const style = asStyleRecord(candidate.style);
            return style.builderLocked !== true && style.builderHidden !== true;
          });
        const startStyle =
          activeBlock.style && typeof activeBlock.style === 'object' && !Array.isArray(activeBlock.style)
            ? { ...(activeBlock.style as Record<string, unknown>) }
            : {};
        const transform = readTransform(startStyle);
        const primaryBoundsMode = resolveEditorBoundsMode(activeBlock);
        const usesContentBounds =
          primaryBoundsMode === 'content' || primaryBoundsMode === 'text-paragraph';
        const primaryStartGapBox =
          primaryGapStartBox ??
          primaryLiveFrame ??
          primaryEditorBounds?.activeBox ??
          primaryEditorBounds?.frameBox;
        const resolvedBlockWidth = Math.max(
          1,
          primaryStartGapBox?.width ??
            (usesContentBounds ? undefined : primaryEditorBounds?.frameBox.width) ??
            (usesContentBounds ? undefined : blockRect?.width) ??
            activeBlock.layout.w * colWidth
        );
        const resolvedBlockHeight = Math.max(
          1,
          primaryStartGapBox?.height ??
            (usesContentBounds ? undefined : primaryEditorBounds?.frameBox.height) ??
            (usesContentBounds ? undefined : blockRect?.height) ??
            activeBlock.layout.h * rowHeight
        );
        const primaryStartFrameBox = primaryStartGapBox ?? undefined;
        const { startOffsetXPx, startOffsetYPx } = resolveFineMoveStartOffsets({
          layout: activeBlock.layout,
          colWidth,
          rowHeight,
          editorBounds: primaryEditorBounds,
          transform,
          blockWidth: resolvedBlockWidth,
          blockHeight: resolvedBlockHeight,
        });
        const groupItems = moveBlocks.map((moveBlock) => {
          const itemStartStyle = asStyleRecord(moveBlock.style);
          const itemTransform = readTransform(itemStartStyle);
          const isPrimary = moveBlock.id === activeBlock.id;
          const itemEditorBounds = editorBoundsByBlockId[moveBlock.id];
          const itemGapStartBox = resolveGapAlignmentBoxFromLayer(moveBlock, layer);
          const itemLiveFrame = resolveLiveActiveBoxFromLayer(layer, moveBlock.id, moveBlock);
          const itemBoundsMode = resolveEditorBoundsMode(moveBlock);
          const itemUsesContentBounds =
            itemBoundsMode === 'content' || itemBoundsMode === 'text-paragraph';
          const itemStartGapBox =
            itemGapStartBox ?? itemLiveFrame ?? itemEditorBounds?.activeBox ?? itemEditorBounds?.frameBox;
          const itemWidth = isPrimary
            ? resolvedBlockWidth
            : Math.max(
                1,
                itemStartGapBox?.width ??
                  (itemUsesContentBounds ? undefined : itemEditorBounds?.frameBox.width) ??
                  moveBlock.layout.w * colWidth * itemTransform.widthScale
              );
          const itemHeight = isPrimary
            ? resolvedBlockHeight
            : Math.max(
                1,
                itemStartGapBox?.height ??
                  (itemUsesContentBounds ? undefined : itemEditorBounds?.frameBox.height) ??
                  moveBlock.layout.h * rowHeight * itemTransform.heightScale
              );
          const { startOffsetXPx: itemOffsetXPx, startOffsetYPx: itemOffsetYPx } = resolveFineMoveStartOffsets({
            layout: moveBlock.layout,
            colWidth,
            rowHeight,
            editorBounds: itemEditorBounds,
            transform: itemTransform,
            blockWidth: itemWidth,
            blockHeight: itemHeight,
          });
          return {
            blockId: moveBlock.id,
            startLayout: { ...moveBlock.layout },
            startStyle: itemStartStyle,
            startOffsetXPx: itemOffsetXPx,
            startOffsetYPx: itemOffsetYPx,
            startEditorBox: itemStartGapBox,
            startFrameBox: itemStartGapBox,
            blockWidth: itemWidth,
            blockHeight: itemHeight,
            nextStyle: itemStartStyle,
          };
        });
        fineMoveRef.current = {
          pointerId: event.pointerId,
          blockId: activeBlock.id,
          viewport,
          startLayout: { ...activeBlock.layout },
          startClientX: event.clientX,
          startClientY: event.clientY,
          startStyle,
          startOffsetXPx,
          startOffsetYPx,
          startEditorBox: primaryStartFrameBox,
          startFrameBox: primaryStartFrameBox,
          colWidth,
          rowHeight,
          maxRows,
          totalColumns,
          referenceBlocks: createReferenceBlocks(selectedMoveIds, colWidth, rowHeight),
          blockWidth: resolvedBlockWidth,
          blockHeight: resolvedBlockHeight,
          nextStyle: startStyle,
          groupItems,
        };
        const startGeometry = buildMovementGeometryPayload({
          block: activeBlock,
          blocks,
          layer,
          colWidth,
          rowHeight,
          editorBoundsByBlockId,
          dragStartBox: primaryStartFrameBox,
        });
        emitMovementDebug({
          source: 'drag',
          phase: 'start',
          blockId: activeBlock.id,
          blockIds: groupItems.map((item) => item.blockId),
          viewport,
          handle: 'move',
          input: {
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
          },
          layout: block.layout,
          transform,
          updates: groupItems.map((item) => ({
            blockId: item.blockId,
            layout: item.startLayout,
            transform: readTransform(item.startStyle),
            startOffsetXPx: item.startOffsetXPx,
            startOffsetYPx: item.startOffsetYPx,
            dom: getMovementDebugBlockSnapshot(item.blockId),
          })),
          dom: getMovementDebugBlockSnapshot(block.id),
          geometryAudit: startGeometry.geometryAudit,
          candidateGeometry: startGeometry.candidateGeometry,
        });
        setIsTransforming(true);
        setAlignmentGuidesIfChanged({});
        event.currentTarget.setPointerCapture?.(event.pointerId);
        return;
      }

      const startStyle = asStyleRecord(block.style);
      const shouldScaleTextOnResize =
        block.type === 'text' &&
        handle !== 'move' &&
        startStyle.textSizingMode === 'scale' &&
        onPreviewBlockStyle &&
        onCommitBlockStyle;
      const blockElement = event.currentTarget.closest('[data-builder-block-id]');

      transformRef.current = {
        pointerId: event.pointerId,
        blockId: block.id,
        blockType: block.type,
        handle,
        viewport,
        startLayout: { ...block.layout },
        lastAppliedLayout: { ...block.layout },
        pendingLayout: { ...block.layout },
        startContentOffsetX: Number((block.content as Record<string, unknown> | undefined)?.contentOffsetX) || 0,
        startContentOffsetY: Number((block.content as Record<string, unknown> | undefined)?.contentOffsetY) || 0,
        startClientX: event.clientX,
        startClientY: event.clientY,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
        startScrollLeft: resolveScrollContainer()?.scrollLeft ?? 0,
        startScrollTop: resolveScrollContainer()?.scrollTop ?? 0,
        colWidth: rect.width / totalColumns,
        rowHeight: rowHeightPx ?? rect.height / maxRows,
        maxRows,
        referenceBlocks: createReferenceBlocks(block.id, rect.width / totalColumns, rowHeightPx ?? rect.height / maxRows),
        startEditorBox: editorBoundsByBlockId[block.id]?.activeBox,
        textScale: shouldScaleTextOnResize
          ? {
              startStyle,
              startFontSize: resolveTextScaleStartFontSize(startStyle, viewport, blockElement),
              lastAppliedStyle: null,
              pendingStyle: null,
            }
          : undefined,
      };
      emitMovementDebug({
        source: handle === 'move' ? 'drag' : 'resize',
        phase: 'start',
        blockId: block.id,
        viewport,
        handle,
        input: {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
        },
        layout: block.layout,
        transform: readTransform(startStyle),
        dom: getMovementDebugBlockSnapshot(block.id),
        note: handle === 'move' ? 'Using grid/layout move path, not fine pixel move path.' : undefined,
      });
      setIsTransforming(true);
      setAlignmentGuidesIfChanged({});
      event.currentTarget.setPointerCapture?.(event.pointerId);
      stopAutoScroll();
      autoScrollRafRef.current = window.requestAnimationFrame(stepAutoScroll);
    },
    [
      blocks,
      createReferenceBlocks,
      enabled,
      editorBoundsByBlockId,
      layerRef,
      maxRows,
      allowRowExpansion,
      onBlockLayoutChange,
      onCommitBlockStyle,
      onDuplicateCanvasBlockForDrag,
      onPreviewBlockStyle,
      resolveScrollContainer,
      rowHeightPx,
      readTransform,
      selectBlock,
      selectedBlockIds,
      stepAutoScroll,
      stopAutoScroll,
      totalColumns,
      viewport,
      writeTransform,
    ]
  );

  const moveTransform = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const interaction = transformRef.current;
      const fineMove = fineMoveRef.current;
      if (fineMove && fineMove.pointerId === event.pointerId) {
        event.preventDefault();
        event.stopPropagation();
        scheduleFineMovePreview(event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
        return;
      }
      if (!interaction || interaction.pointerId !== event.pointerId || !onBlockLayoutChange) return;
      event.preventDefault();
      event.stopPropagation();
      interaction.lastClientX = event.clientX;
      interaction.lastClientY = event.clientY;
      applyTransformAtPointer(interaction, event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
    },
    [applyTransformAtPointer, onBlockLayoutChange, scheduleFineMovePreview]
  );

  const endTransform = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const fineMove = fineMoveRef.current;
    if (fineMove && fineMove.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopPropagation();
      finishFineMove(event.pointerId, event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      return;
    }
    const interaction = transformRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    finishLayoutTransform(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [
    finishFineMove,
    finishLayoutTransform,
  ]);

  React.useEffect(() => {
    if (!isTransforming || typeof window === 'undefined') return;

    const handlePointerMove = (event: PointerEvent) => {
      const fineMove = fineMoveRef.current;
      if (fineMove?.isPlacementDrag) {
        fineMovePointerRef.current = {
          clientX: event.clientX,
          clientY: event.clientY,
          snapDisabled: isDragSnapDisabled(event),
          shiftKey: event.shiftKey,
        };
        scheduleFineMovePreview(event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
        return;
      }
      if (fineMove && fineMove.pointerId === event.pointerId) {
        scheduleFineMovePreview(event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
        return;
      }

      const interaction = transformRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || !onBlockLayoutChange) return;
      interaction.lastClientX = event.clientX;
      interaction.lastClientY = event.clientY;
      applyTransformAtPointer(interaction, event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
    };

    const handlePointerEnd = (event: PointerEvent) => {
      const fineMove = fineMoveRef.current;
      if (fineMove?.isPlacementDrag) {
        finishFineMove(PLACEMENT_POINTER_ID, event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey);
        return;
      }
      if (finishFineMove(event.pointerId, event.clientX, event.clientY, isDragSnapDisabled(event), event.shiftKey)) return;
      finishLayoutTransform(event.pointerId);
    };

    window.addEventListener('pointermove', handlePointerMove, true);
    window.addEventListener('pointerup', handlePointerEnd, true);
    window.addEventListener('pointercancel', handlePointerEnd, true);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove, true);
      window.removeEventListener('pointerup', handlePointerEnd, true);
      window.removeEventListener('pointercancel', handlePointerEnd, true);
    };
  }, [
    applyTransformAtPointer,
    finishFineMove,
    finishLayoutTransform,
    isTransforming,
    onBlockLayoutChange,
    scheduleFineMovePreview,
  ]);

  React.useEffect(() => {
    if (!isPlacementDrag || typeof window === 'undefined') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!fineMoveRef.current?.isPlacementDrag) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelPlacementDrag();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commitPlacementDrag();
        return;
      }
      const step = event.shiftKey ? 8 : 1;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        nudgePlacementDrag(-step, 0);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        nudgePlacementDrag(step, 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        nudgePlacementDrag(0, -step);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        nudgePlacementDrag(0, step);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [cancelPlacementDrag, commitPlacementDrag, isPlacementDrag, nudgePlacementDrag]);

  React.useEffect(() => {
    if (!enabled) {
      setIsTransforming(false);
      setIsPlacementDrag(false);
      setAlignmentGuidesIfChanged({});
      transformRef.current = null;
      fineMoveRef.current = null;
      if (fineMoveRafRef.current != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(fineMoveRafRef.current);
      }
      fineMoveRafRef.current = null;
      fineMovePointerRef.current = null;
      stopAutoScroll();
    }
  }, [enabled, setAlignmentGuidesIfChanged, stopAutoScroll]);

  React.useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);

  return {
    alignmentGuides,
    isTransforming,
    isPlacementDrag,
    beginTransform,
    beginPlacementDrag,
    moveTransform,
    endTransform,
    selectBlock,
  };
}
